import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDealer } from '../context/DealerContext';
import { useLanguage } from '../context/LanguageContext';
import { LogIn, Eye, EyeOff, Shield } from 'lucide-react';

const texts = {
  ru: {
    title: 'Кабинет Дилера',
    subtitle: 'Войдите для доступа к каталогу, ценам и инструментам',
    email: 'Логин',
    password: 'Пароль',
    login: 'Войти',
    logging: 'Вход...',
    error: 'Неверный логин или пароль',
    contact: 'Нет аккаунта? Свяжитесь с нами',
  },
  kz: {
    title: 'Дилер кабинеті',
    subtitle: 'Каталог, бағалар мен құралдарға қол жеткізу үшін кіріңіз',
    email: 'Логин',
    password: 'Құпия сөз',
    login: 'Кіру',
    logging: 'Кіру...',
    error: 'Қате логин немесе құпия сөз',
    contact: 'Тіркелгі жоқ па? Бізбен хабарласыңыз',
  },
  en: {
    title: 'Dealer Cabinet',
    subtitle: 'Log in to access catalog, prices and tools',
    email: 'Login',
    password: 'Password',
    login: 'Log In',
    logging: 'Logging in...',
    error: 'Invalid email or password',
    contact: "Don't have an account? Contact us",
  },
};

export default function DealerLoginPage() {
  const { login } = useDealer();
  const { language } = useLanguage();
  const t = texts[language] || texts.ru;
  const navigate = useNavigate();

  const [loginValue, setLoginValue] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(loginValue, password);
      navigate('/dealer');
    } catch (err) {
      setError(err.message || t.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#061d36] flex items-center justify-center px-4 py-20" data-testid="dealer-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#8b4444]/20 mb-4">
            <Shield className="w-8 h-8 text-[#8b4444]" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">{t.title}</h1>
          <p className="text-white/50 text-sm">{t.subtitle}</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 space-y-5">
          <div>
            <label className="block text-white/60 text-sm mb-2">{t.email}</label>
            <input
              type="text" required value={loginValue} onChange={e => setLoginValue(e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#8b4444] transition-colors"
              placeholder=""
              data-testid="dealer-email-input"
            />
          </div>

          <div>
            <label className="block text-white/60 text-sm mb-2">{t.password}</label>
            <div className="relative">
              <input
                type={showPass ? 'text' : 'password'} required value={password} onChange={e => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#8b4444] transition-colors pr-12"
                data-testid="dealer-password-input"
              />
              <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60">
                {showPass ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-red-400 text-sm" data-testid="dealer-login-error">
              {error}
            </div>
          )}

          <button
            type="submit" disabled={loading}
            className="w-full py-3.5 bg-[#8b4444] hover:bg-[#7a3838] text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="dealer-login-btn"
          >
            {loading ? t.logging : <><LogIn className="w-5 h-5" />{t.login}</>}
          </button>
        </form>
      </div>
    </div>
  );
}
